import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth, startGoogleSignIn } from "../lib/auth";
import { api } from "../lib/api";
import {
  Megaphone, Check, ChevronDown, ChevronUp, Mail, ShieldCheck, AlertCircle, ExternalLink, Heart,
} from "lucide-react";

const STEPS = [
  {
    n: "01",
    title: "Sign in with your Google account",
    body: "Use the blue 'Sign in with Google' button on this page. You'll be taken to a Google screen — pick your account and approve. We never see or store your Google password.",
  },
  {
    n: "02",
    title: "Submit your sponsor ad",
    body: "After signing in, you'll land on your dashboard. Click 'New Campaign' and fill in your business name, ad headline (≤80 chars), the URL of your ad image (a public square or 4:3 image, 600×450 ideal), and the URL you want clicks to go to.",
  },
  {
    n: "03",
    title: "Send your $100 sponsor donation",
    body: "Choose any of: PayPal, Cash App ($imnono7733), or Zelle (info@pawsonrecord.com). Donate $100 USD. In the payment note, put your business name and your campaign ID (shown on your dashboard). This is a donation, not a recurring subscription — you decide each month whether to give again.",
  },
  {
    n: "04",
    title: "We approve and your ad goes live",
    body: "After we receive your donation and review your creative (usually within 24 hours), your ad appears in the public sidebar across the registry. We'll email you a confirmation.",
  },
  {
    n: "05",
    title: "Give again whenever you'd like",
    body: "Your ad runs for 30 days per $100 donation. Want to keep it going? Just donate another $100 before day 30 and your slot refreshes. We'll send a friendly reminder ~5 days before your slot expires — no automatic charges, ever.",
  },
];

const FAQ_TROUBLESHOOT = [
  {
    q: "I clicked 'Sign in with Google' and nothing happened.",
    a: "Most browsers block sign-in popups by default. Allow popups for paws-on-record (look for a small icon in your browser's address bar after clicking the button), or try opening this page in an Incognito / Private window where extensions are disabled.",
  },
  {
    q: "Google says 'access blocked' or 'this app isn't verified'.",
    a: "This usually means a corporate or school Google account has restrictions. Try signing in with your personal Gmail account, or ask your IT admin to allow third-party Google sign-in.",
  },
  {
    q: "I'm signed in but I can't see my dashboard.",
    a: "Sign out from the top-right menu and sign in again. If your browser is in 'strict' privacy mode, third-party cookies can be blocked — open paws-on-record in a normal Chrome / Safari / Firefox window.",
  },
  {
    q: "My image isn't showing up in the preview.",
    a: "The image must be a publicly accessible URL ending in .jpg / .png / .webp. Right-click the image you uploaded somewhere, choose 'Copy image address', and paste that exact link. Imgur and your own website are good hosts; Google Drive share links won't work.",
  },
  {
    q: "I donated but my ad still says 'pending donation'.",
    a: "We mark donations received manually within 24 hours. If 24 hours have passed, email us with your payment receipt screenshot and your campaign ID — we'll fix it the same day.",
  },
];

export default function Advertise() {
  const { user } = useAuth();
  const [openIdx, setOpenIdx] = useState(0);
  const [stats, setStats] = useState(null);
  const [sponsorCount, setSponsorCount] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/abusers/stats")
      .then(({ data }) => { if (alive) setStats(data); })
      .catch(() => {});
    api.get("/campaigns/active", { params: { limit: 1 } })
      .then(({ data }) => { if (alive) setSponsorCount(data.total_active ?? 0); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const totalRecords = stats?.total ?? null;
  const stateCount = stats?.by_state?.length ?? null;
  const crimeCount = stats?.by_crime?.length ?? null;

  return (
    <div data-testid="advertise-page" className="bg-white">
      {/* Hero */}
      <section className="border-b-2 border-zinc-900 bg-gradient-to-br from-amber-50 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 border border-zinc-900 bg-amber-300 px-3 py-1.5 text-[11px] uppercase tracking-widest font-bold mb-6">
              <Heart size={12} /> Sponsor donations · Paws on Record
            </div>
            <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
              Sponsor <span className="text-red-600">animal-rescue visibility</span><br />
              with a <span className="underline decoration-amber-400 decoration-8 underline-offset-2">$100 donation.</span>
            </h1>
            <p className="mt-6 text-lg text-zinc-700 max-w-2xl leading-relaxed">
              Shelters, rescues, fosters, vets, and adoption-minded families browse this registry every day to vet
              applicants. <strong>Donate $100</strong> and your business gets a small "Sponsored" card in the sidebar for 30 days.
              <strong> No subscription. No auto-billing.</strong> Each month you decide whether to give again.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              {user ? (
                <Link
                  to="/my-campaigns"
                  className="inline-flex items-center gap-2 bg-zinc-900 text-white px-6 py-3 font-bold uppercase tracking-widest text-sm hover:bg-red-600 transition-colors"
                  data-testid="advertise-go-dashboard"
                >
                  Go to my dashboard <ExternalLink size={14} />
                </Link>
              ) : (
                <button
                  type="button"
                  onClick={() => startGoogleSignIn("/my-campaigns")}
                  className="inline-flex items-center gap-2 bg-white border-2 border-zinc-900 text-zinc-900 px-6 py-3 font-bold uppercase tracking-widest text-sm hover:bg-zinc-900 hover:text-white transition-colors shadow-[4px_4px_0_0_#18181b]"
                  data-testid="advertise-signin-btn"
                >
                  <GoogleIcon /> Sign in with Google
                </button>
              )}
              <a
                href="#how-it-works"
                className="inline-flex items-center gap-2 px-6 py-3 font-bold uppercase tracking-widest text-sm border border-zinc-300 hover:border-zinc-900"
                data-testid="advertise-how-it-works-link"
              >
                How it works
              </a>
            </div>
            <div className="mt-4 flex items-center gap-2 text-xs text-zinc-600 font-mono">
              <ShieldCheck size={13} /> Email support: <a className="underline" href="mailto:info@pawsonrecord.com">info@pawsonrecord.com</a>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="border-2 border-zinc-900 bg-white p-6 shadow-[8px_8px_0_0_#18181b]">
              <div className="label-eyebrow mb-2">What you get</div>
              <ul className="space-y-3 mt-4">
                {[
                  "A 'Sponsored' card in the registry sidebar for 30 days per donation",
                  "Your image, headline, business name, and click-through link",
                  "Nationwide visibility — no geo restrictions",
                  "Clear 'Sponsored' label (FTC-compliant disclosure)",
                  "Edit your creative any time from your dashboard",
                  "Never a recurring charge — you decide each month whether to give again",
                ].map((s) => (
                  <li key={s} className="flex items-start gap-2 text-sm">
                    <Check size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6 pt-6 border-t border-zinc-200 flex items-baseline justify-between">
                <span className="font-mono text-xs uppercase text-zinc-500">Suggested sponsor donation</span>
                <span className="font-display font-black text-3xl">
                  $100<span className="text-base font-mono text-zinc-500"> / 30 days</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Live audience stats — credibility badge */}
      <section className="border-b border-zinc-900 bg-zinc-900 text-white" data-testid="advertise-stats">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 items-center">
            <Stat
              num={totalRecords !== null ? `${totalRecords}+` : "—"}
              label="Convicted records"
              testId="advertise-stat-records"
            />
            <Stat
              num={stateCount !== null ? `${stateCount}` : "—"}
              label="U.S. states + D.C."
              testId="advertise-stat-states"
            />
            <Stat
              num={crimeCount !== null ? `${crimeCount}` : "—"}
              label="Crime categories"
              testId="advertise-stat-crimes"
            />
            {sponsorCount !== null && sponsorCount > 0 ? (
              <Stat
                num={`${sponsorCount}`}
                label={sponsorCount === 1 ? "Sponsor live now" : "Sponsors live now"}
                testId="advertise-stat-sponsors"
                live
              />
            ) : (
              <div data-testid="advertise-stat-sponsors-empty">
                <div className="font-display font-black text-lg sm:text-xl tracking-tight text-amber-300 leading-tight">
                  Be the first<br/>sponsor →
                </div>
                <div className="text-[11px] mt-2 font-mono uppercase tracking-widest text-zinc-300">
                  No ads yet — you'll own the slot
                </div>
              </div>
            )}
          </div>
          <p className="mt-6 text-xs font-mono text-zinc-400 max-w-3xl">
            Shelters, fosters, rescue volunteers, and adoption-minded families across all 50 states use Paws on Record
            to vet applicants before placing animals. Your sidebar ad reaches them at the moment they're already engaged
            in animal-welfare decisions.
          </p>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="max-w-5xl mx-auto px-6 lg:px-8 py-20">
        <div className="label-eyebrow mb-3">Step-by-step</div>
        <h2 className="font-display font-black text-3xl sm:text-4xl mb-10 tracking-tight">
          How to launch your ad in 5 steps
        </h2>
        <ol className="space-y-6">
          {STEPS.map((s) => (
            <li key={s.n} className="grid grid-cols-[60px_1fr] gap-5 border-l-2 border-zinc-900 pl-5 py-2">
              <div className="font-display font-black text-3xl text-red-600">{s.n}</div>
              <div>
                <div className="font-display font-bold text-lg">{s.title}</div>
                <p className="text-sm text-zinc-700 mt-1 leading-relaxed">{s.body}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* Payment */}
      <section className="bg-zinc-900 text-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 py-16">
          <div className="label-eyebrow text-amber-300 mb-3">Donate $100 to sponsor a slot</div>
          <h2 className="font-display font-black text-3xl sm:text-4xl mb-8 tracking-tight">
            Three ways to donate
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="border border-zinc-700 bg-zinc-800 p-6">
              <div className="font-display font-bold text-lg">Cash App</div>
              <div className="font-mono text-amber-300 text-xl mt-2 select-all" data-testid="pay-cashapp">$imnono7733</div>
              <p className="text-xs text-zinc-400 mt-3">Open Cash App → Pay → enter cashtag → $100. In the note add: <span className="text-white">your business name + campaign ID</span>.</p>
            </div>
            <div className="border border-zinc-700 bg-zinc-800 p-6">
              <div className="font-display font-bold text-lg">Zelle</div>
              <div className="font-mono text-amber-300 text-base mt-2 select-all" data-testid="pay-zelle">info@pawsonrecord.com</div>
              <p className="text-xs text-zinc-400 mt-3">Open your bank app's Zelle tool → Send $100 to that email. Memo: <span className="text-white">business name + campaign ID</span>.</p>
            </div>
            <div className="border border-zinc-700 bg-zinc-800 p-6">
              <div className="font-display font-bold text-lg">PayPal</div>
              <div className="font-mono text-amber-300 text-base mt-2 select-all" data-testid="pay-paypal">info@pawsonrecord.com</div>
              <p className="text-xs text-zinc-400 mt-3">Send $100 USD as a personal payment to that email. Note: <span className="text-white">business name + campaign ID</span>.</p>
            </div>
          </div>
          <p className="mt-6 text-xs font-mono text-zinc-400 max-w-3xl">
            Once we see your donation we mark your campaign approved and your ad goes live.
            There's no auto-renewal and no card on file — we'll just email a friendly reminder ~5 days
            before your 30-day slot expires so you can decide whether to give again.
          </p>
        </div>
      </section>

      {/* Troubleshooting */}
      <section id="troubleshooting" className="max-w-4xl mx-auto px-6 lg:px-8 py-20">
        <div className="label-eyebrow mb-3 flex items-center gap-2">
          <AlertCircle size={12} /> Stuck? Read this first
        </div>
        <h2 className="font-display font-black text-3xl sm:text-4xl mb-3 tracking-tight">
          Troubleshooting
        </h2>
        <p className="text-sm text-zinc-600 mb-8 max-w-2xl">
          99% of sign-in problems are caused by browser pop-up blockers, third-party cookie restrictions,
          or restricted Google Workspace accounts. Walk through these before contacting support.
        </p>

        <div className="space-y-2">
          {FAQ_TROUBLESHOOT.map((item, i) => {
            const open = openIdx === i;
            return (
              <div key={item.q} className="border border-zinc-300">
                <button
                  type="button"
                  onClick={() => setOpenIdx(open ? -1 : i)}
                  className="w-full text-left flex items-center justify-between gap-4 px-5 py-4 hover:bg-zinc-50 transition-colors"
                  data-testid={`troubleshoot-q-${i}`}
                >
                  <span className="font-display font-bold text-sm">{item.q}</span>
                  {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
                {open && (
                  <div className="px-5 pb-5 text-sm text-zinc-700 leading-relaxed border-t border-zinc-200 pt-4">
                    {item.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Support fallback — mailto + Google official help links (NO fake "Google tech number") */}
        <div className="mt-10 border-l-4 border-red-600 bg-red-50 p-6">
          <div className="font-display font-bold mb-2 flex items-center gap-2">
            <Mail size={16} /> Still stuck?
          </div>
          <p className="text-sm text-zinc-700 leading-relaxed">
            Email us a screenshot of what you see and we'll help personally — usually within 24 hours.
            <br /><br />
            <a
              href="mailto:info@pawsonrecord.com?subject=Paws%20on%20Record%20Ad%20Help"
              className="inline-flex items-center gap-2 bg-zinc-900 text-white px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-red-600 transition-colors"
              data-testid="advertise-support-mailto"
            >
              <Mail size={12} /> Email support
            </a>
          </p>

          <div className="mt-6 pt-6 border-t border-red-200">
            <div className="font-display font-bold text-sm mb-2">If your problem is with Google itself</div>
            <p className="text-xs text-zinc-600 leading-relaxed">
              For Google account / sign-in / 2-step-verification issues that are not specific to Paws on Record,
              go directly to Google's official help center. <strong>We do not list a "Google support phone number"
              because Google does not operate a public phone line for sign-in issues — almost every "Google tech support"
              number you see online is a scam.</strong> Use these official links instead:
            </p>
            <ul className="mt-3 space-y-1.5 text-xs">
              <li>
                <a
                  href="https://support.google.com/accounts"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-red-700 underline font-semibold"
                  data-testid="google-help-accounts"
                >
                  Google Account Help <ExternalLink size={11} />
                </a>
              </li>
              <li>
                <a
                  href="https://accounts.google.com/signin/recovery"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-red-700 underline font-semibold"
                  data-testid="google-help-recovery"
                >
                  Google Account recovery <ExternalLink size={11} />
                </a>
              </li>
              <li>
                <a
                  href="https://support.google.com/accounts/answer/7682439"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-red-700 underline font-semibold"
                  data-testid="google-help-scams"
                >
                  How to spot fake "Google tech support" scams <ExternalLink size={11} />
                </a>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="border-t-2 border-zinc-900 bg-amber-50">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 py-16 text-center">
          <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight">
            Ready to put your business in front of rescuers?
          </h2>
          <p className="mt-4 text-zinc-700">$100 donation = 30-day sponsor slot. Sign in with Google to start.</p>
          <div className="mt-6">
            {user ? (
              <Link
                to="/my-campaigns"
                className="inline-flex items-center gap-2 bg-zinc-900 text-white px-8 py-4 font-bold uppercase tracking-widest text-sm hover:bg-red-600 transition-colors"
                data-testid="advertise-final-go-dashboard"
              >
                Go to my dashboard
              </Link>
            ) : (
              <button
                type="button"
                onClick={() => startGoogleSignIn("/my-campaigns")}
                className="inline-flex items-center gap-2 bg-white border-2 border-zinc-900 text-zinc-900 px-8 py-4 font-bold uppercase tracking-widest text-sm hover:bg-zinc-900 hover:text-white transition-colors shadow-[4px_4px_0_0_#18181b]"
                data-testid="advertise-final-signin-btn"
              >
                <GoogleIcon /> Sign in with Google
              </button>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16.1 19 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.6 8.4 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.1 5.6l6.2 5.2c-.4.4 6.6-4.8 6.6-14.8 0-1.3-.1-2.4-.4-3.5z"/>
    </svg>
  );
}

function Stat({ num, label, testId }) {
  return (
    <div data-testid={testId}>
      <div className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter text-amber-300">
        {num}
      </div>
      <div className="text-[11px] mt-1 font-mono uppercase tracking-widest text-zinc-300">
        {label}
      </div>
    </div>
  );
}
